import * as React from "react";
import {Box, TableSortLabel} from "@mui/material";

export type SortDirection = "asc" | "desc";
export type SortEntry = { column: string; direction: SortDirection };

/** asc → desc → off; newly added columns become primary (front of stack). */
export function cycleSortStack(stack: SortEntry[], column: string): SortEntry[] {
    const idx = stack.findIndex((s) => s.column === column);
    if (idx < 0) {
        return [{column, direction: "asc"}, ...stack];
    }
    if (stack[idx].direction === "asc") {
        return stack.map((s, i) => (i === idx ? {...s, direction: "desc" as const} : s));
    }
    return stack.filter((_, i) => i !== idx);
}

export function getSortMeta(stack: SortEntry[], column: string) {
    const idx = stack.findIndex((s) => s.column === column);
    if (idx < 0) {
        return {active: false, direction: undefined as SortDirection | undefined, priority: undefined as number | undefined};
    }
    return {active: true, direction: stack[idx].direction, priority: idx + 1};
}

export function applyMultiSort<T>(
    rows: T[],
    stack: SortEntry[],
    compareRow: (a: T, b: T, column: string) => number,
): T[] {
    if (!stack.length) return rows;
    const out = [...rows];
    out.sort((a, b) => {
        for (const {column, direction} of stack) {
            const cmp = compareRow(a, b, column);
            if (cmp !== 0) return direction === "desc" ? -cmp : cmp;
        }
        return 0;
    });
    return out;
}

export function parseSortStack(raw: any, defaultStack: SortEntry[]): SortEntry[] {
    if (Array.isArray(raw?.sortStack) && raw.sortStack.length) {
        return raw.sortStack.filter(
            (s: any) => s?.column && (s.direction === "asc" || s.direction === "desc"),
        );
    }
    if (raw?.sort) {
        const dir: SortDirection = raw.orderBy === "asc" ? "asc" : "desc";
        return [{column: String(raw.sort), direction: dir}];
    }
    return defaultStack;
}

export function sortStackForPreset(stack: SortEntry[]) {
    const primary = stack[0];
    return {
        sortStack: stack,
        sort: primary?.column ?? "",
        orderBy: primary?.direction ?? "desc",
    };
}

type ConversionStatSortLabelProps = {
    column: string;
    label: React.ReactNode;
    sortStack: SortEntry[];
    onSortStackChange: (stack: SortEntry[]) => void;
    align?: "inherit" | "left" | "center" | "right" | "justify";
};

export function ConversionStatSortLabel({
    column,
    label,
    sortStack,
    onSortStackChange,
    align,
}: ConversionStatSortLabelProps) {
    const meta = getSortMeta(sortStack, column);
    return (
        <TableSortLabel
            active={meta.active}
            direction={meta.direction ?? "asc"}
            align={align}
            onClick={() => onSortStackChange(cycleSortStack(sortStack, column))}
        >
            {label}
            {meta.priority != null && sortStack.length > 1 && (
                <Box
                    component="span"
                    sx={{
                        fontSize: "0.65rem",
                        fontWeight: 700,
                        opacity: 0.65,
                        ml: 0.25,
                        verticalAlign: "super",
                    }}
                >
                    {meta.priority}
                </Box>
            )}
        </TableSortLabel>
    );
}
